import { useMemo, useState } from 'react';
import { cn } from '../../utils/cn';

/**
 * Çoklu seçim (chips) bileşeni — segment/müdürlük seçici olarak kullanılır.
 *
 * Props:
 *   label              – alan etiketi (opsiyonel)
 *   options            – [{ value, label, hint }] şeklinde dizi
 *   value              – seçili değerlerin dizisi (controlled)
 *   onChange(next)     – seçim değiştiğinde çağrılır
 *   loading            – liste yükleniyor mu?
 *   error              – hata mesajı (opsiyonel)
 *   hint               – yardım metni (opsiyonel)
 *   emptyLabel         – liste boşsa gösterilecek mesaj
 *   searchable         – arama inputu açık/kapalı (default true, >8 opsiyonda önerilir)
 *   required           – zorunlu göstergesi
 *   disabled           – tüm chip'leri disable eder
 *
 * Seçili değerler hem chip rozeti olarak hem de aşağıdaki "Seçili" özetinde gösterilir.
 */
export default function MultiSelectChips({
  label,
  options = [],
  value = [],
  onChange,
  loading = false,
  error,
  hint,
  emptyLabel = 'Liste boş',
  searchable = true,
  required = false,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(value || []), [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLocaleLowerCase('tr');
    return options.filter((o) => {
      const txt = `${o.label || ''} ${o.value || ''}`.toLocaleLowerCase('tr');
      return txt.includes(q);
    });
  }, [options, query]);

  const toggle = (val) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange?.(Array.from(next));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange?.([]);
  };

  const showSearch = searchable && options.length > 8;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
          {required && <span className="text-danger-600 ml-0.5">*</span>}
        </label>
      )}

      {loading && (
        <p className="text-xs text-neutral-500 py-2">Yükleniyor…</p>
      )}

      {!loading && options.length === 0 && (
        <p className="text-xs text-neutral-500 py-2">{emptyLabel}</p>
      )}

      {!loading && options.length > 0 && (
        <>
          {showSearch && (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ara…"
              className="w-full mb-2 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          )}
          <div
            className={cn(
              'flex flex-wrap gap-1.5 rounded-md border p-2 max-h-40 overflow-y-auto bg-white',
              error ? 'border-danger-400' : 'border-neutral-200',
            )}
          >
            {filtered.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  disabled={disabled}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition-colors',
                    checked
                      ? 'bg-primary-50 border-primary-400 text-primary-800'
                      : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400',
                    disabled && 'opacity-60 cursor-not-allowed',
                  )}
                  title={opt.hint}
                >
                  <span
                    className={cn(
                      'inline-block w-3 h-3 rounded-sm border flex items-center justify-center',
                      checked
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'border-neutral-400',
                    )}
                  >
                    {checked && (
                      <svg
                        viewBox="0 0 12 12"
                        className="w-2.5 h-2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M2.5 6l2.5 2.5L9.5 3.5" />
                      </svg>
                    )}
                  </span>
                  <span>{opt.label}</span>
                  {opt.hint && (
                    <span className="text-[10px] text-neutral-500">{opt.hint}</span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <span className="text-xs text-neutral-500">Eşleşen kayıt yok</span>
            )}
          </div>

          {selected.size > 0 && (
            <div className="mt-1.5 flex items-center gap-2 text-xs text-neutral-600">
              <span>{selected.size} seçili</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-primary-600 hover:underline"
                disabled={disabled}
              >
                Temizle
              </button>
            </div>
          )}
        </>
      )}

      {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
      {!error && hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}
